import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { addDeliveryLine, DeliveryOrderConflictError } from '@apis/logistics';
import type { IJoinWrite } from '../../LogisticsPage/orderHandoff';

/** A write the server refused (or never answered), with why. */
export type IJoinFailure = IJoinWrite & { error: unknown };

/** Which refusal the checkout names. Anything the server did not explain — a
 *  line with no room for another task, a dropped connection — says the same
 *  neutral thing, because there is nothing the user can do differently about
 *  any of them from here. */
export function joinErrorKey(error: unknown): string {
  if (error instanceof DeliveryOrderConflictError) {
    if (error.reason === 'DELIVERY_NOT_OPEN') return 'scheduler.panel.stock.join.errorArrived';
    if (error.reason === 'UNIT_MISMATCH') return 'scheduler.panel.stock.join.errorUnit';
    // The material is open on a DIFFERENT truck, or this one is full: both are
    // "not onto this order", and neither is fixable from here.
    if (error.reason === 'LINE_OPEN_ELSEWHERE') return 'scheduler.panel.stock.join.errorOpenElsewhere';
    if (error.reason === 'LINES_FULL') return 'scheduler.panel.stock.join.errorFull';
  }
  return 'scheduler.panel.stock.join.error';
}

/**
 * The checkout's adds to orders already on their way — from the review
 * ("Add to #ref") and after the new order's Create alike.
 *
 * One write at a time, in order: each grows an order's line and this task's
 * share of it, and a refusal of one says nothing about the next, so every
 * write is tried and the refused ones come back. The site payload is
 * re-read afterwards either way — what landed changes what the list says.
 */
export function useJoinWrites(siteId: string) {
  const qc = useQueryClient();
  const [pending, setPending] = useState(false);

  const run = async (writes: IJoinWrite[]): Promise<IJoinFailure[]> => {
    setPending(true);
    const failures: IJoinFailure[] = [];
    for (const write of writes) {
      try {
        await addDeliveryLine(siteId, write.ref, write.body, null);
      } catch (error) {
        failures.push({ ...write, error });
      }
    }
    setPending(false);
    void qc.invalidateQueries({ queryKey: ['logistics', siteId] });
    return failures;
  };

  return { run, pending };
}
